import * as THREE from 'three';

/**
 * Represents the bow's visual model
 */
export class BowModel {
  // Bow object
  private object: THREE.Object3D;
  
  // Bow parts
  private bowBody: THREE.Mesh;
  private bowString: THREE.Line;
  private arrow: THREE.Object3D;
  private bowCapTop: THREE.Mesh;
  private bowCapBottom: THREE.Mesh;
  
  // Bow state
  private drawProgress: number = 0;
  private isArrowVisible: boolean = false;
  
  /**
   * Creates a new BowModel
   */
  constructor() {
    // Create bow object
    this.object = new THREE.Object3D();
    
    // Create bow body
    const bowGeometry = new THREE.TorusGeometry(0.3, 0.02, 16, 32, Math.PI);
    const bowMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    this.bowBody = new THREE.Mesh(bowGeometry, bowMaterial);
    
    // Rotate bow 90 degrees counter-clockwise (around Z axis)
    this.bowBody.rotation.z = Math.PI / 2; // This rotates it counter-clockwise
    
    // Create caps for the ends of the bow
    const capGeometry = new THREE.CircleGeometry(0.02, 16);
    const capMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x8B4513,
      side: THREE.DoubleSide // Make the cap visible from both sides
    });
    
    // Create top cap
    this.bowCapTop = new THREE.Mesh(capGeometry, capMaterial);
    this.bowCapTop.position.set(0, 0.3, 0);  // Vertical position (top)
    this.bowCapTop.rotation.x = Math.PI / 2; // Rotate to face outward
    
    // Create bottom cap
    this.bowCapBottom = new THREE.Mesh(capGeometry, capMaterial);
    this.bowCapBottom.position.set(0, -0.3, 0);  // Vertical position (bottom)
    this.bowCapBottom.rotation.x = -Math.PI / 2; // Rotate to face outward
    
    // Create bow string - vertical
    const stringGeometry = new THREE.BufferGeometry();
    const stringMaterial = new THREE.LineBasicMaterial({ color: 0xFFFFFF });
    const stringPoints = [
      new THREE.Vector3(0, 0.3, 0),  // Top point (vertical)
      new THREE.Vector3(0, -0.3, 0)  // Bottom point (vertical)
    ];
    stringGeometry.setFromPoints(stringPoints);
    this.bowString = new THREE.Line(stringGeometry, stringMaterial);
    
    // Create arrow container
    this.arrow = new THREE.Object3D();
    
    // Create arrow shaft - back to using cylinder for better appearance
    const arrowShaftGeometry = new THREE.CylinderGeometry(0.01, 0.01, 0.6, 8);
    const arrowShaftMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const arrowShaft = new THREE.Mesh(arrowShaftGeometry, arrowShaftMaterial);
    
    // Create arrow head
    const arrowHeadGeometry = new THREE.ConeGeometry(0.03, 0.1, 8);
    const arrowHeadMaterial = new THREE.MeshStandardMaterial({ color: 0x808080 });
    const arrowHead = new THREE.Mesh(arrowHeadGeometry, arrowHeadMaterial);
    
    // Position arrow parts
    arrowHead.position.y = 0.35; // Position at the end of the shaft
    
    // Add parts to arrow
    this.arrow.add(arrowShaft);
    this.arrow.add(arrowHead);
    
    // Rotate the arrow to point forward
    this.arrow.rotation.z = Math.PI / 2; // Make shaft horizontal
    
    // Position arrow at the middle of the bow string
    // Adjust the position so the end of the arrow touches the string
    // The arrow is 0.6 units long, rotated to horizontal, so we need to offset it by half its length
    this.arrow.position.set(-0.3, 0, 0.05);
    this.arrow.visible = false;
    
    // Add parts to object
    this.object.add(this.bowBody);
    this.object.add(this.bowString);
    this.object.add(this.arrow);
    this.object.add(this.bowCapTop);
    this.object.add(this.bowCapBottom);
    
    console.log("BowModel created with vertical string and arrow touching the string");
  }
  
  /**
   * Gets the bow object
   * @returns The bow object
   */
  public getObject(): THREE.Object3D {
    return this.object;
  }
  
  /**
   * Starts drawing the bow
   */
  public startDrawing(): void {
    this.arrow.visible = true;
    this.isArrowVisible = true;
    this.setDrawProgress(0);
  }
  
  /**
   * Sets the draw progress
   * @param progress The draw progress (0-1)
   */
  public setDrawProgress(progress: number): void {
    this.drawProgress = Math.max(0, Math.min(1, progress));
    
    // Update string position for vertical bow
    const stringPoints = [
      new THREE.Vector3(0, 0.3, 0),  // Top point
      new THREE.Vector3(0, -0.3, 0)  // Bottom point
    ];
    
    // Bend string based on draw progress - adjusted for vertical bow
    const midpoint = new THREE.Vector3(
      this.drawProgress * 0.2,  // Pull string to the right (positive X)
      0,
      0
    );
    
    // Update string geometry
    const stringGeometry = new THREE.BufferGeometry();
    stringGeometry.setFromPoints([
      stringPoints[0],
      midpoint,
      stringPoints[1]
    ]);
    
    (this.bowString.geometry as THREE.BufferGeometry).dispose();
    this.bowString.geometry = stringGeometry;
    
    // Update arrow position - now horizontal and following the string
    if (this.isArrowVisible) {
      // Move arrow to match string midpoint, keeping the end touching the string
      this.arrow.position.x = -0.3 + this.drawProgress * 0.2;
      
      // Adjust arrow position to stay centered on the string
      this.arrow.position.y = 0;
    }
  }
  
  /**
   * Releases the arrow
   */
  public releaseArrow(): void {
    this.arrow.visible = false;
    this.isArrowVisible = false;
    this.setDrawProgress(0);
  }
  
  /**
   * Cleans up resources when the bow model is no longer needed
   */
  public dispose(): void {
    // Dispose of bow body
    if (this.bowBody.geometry) {
      this.bowBody.geometry.dispose();
    }
    
    if (this.bowBody.material instanceof THREE.Material) {
      this.bowBody.material.dispose();
    }
    
    // Dispose of bow caps
    if (this.bowCapTop.geometry) {
      this.bowCapTop.geometry.dispose();
    }
    
    if (this.bowCapTop.material instanceof THREE.Material) {
      this.bowCapTop.material.dispose();
    }
    
    if (this.bowCapBottom.geometry) {
      this.bowCapBottom.geometry.dispose();
    }
    
    if (this.bowCapBottom.material instanceof THREE.Material) {
      this.bowCapBottom.material.dispose();
    }
    
    // Dispose of bow string
    if (this.bowString.geometry) {
      this.bowString.geometry.dispose();
    }
    
    if (this.bowString.material instanceof THREE.Material) {
      this.bowString.material.dispose();
    }
    
    // Dispose of arrow
    this.arrow.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
} 